/**
 * OCR for images and image-only PDF pages. Lazy-loads tesseract.js the
 * first time anyone needs it. A single in-process queue serialises
 * recognise() calls because each Tesseract worker eats ~200 MB of
 * memory and we'd OOM mobile WebKit if we ran them in parallel.
 *
 * The function returns the full extracted text plus a flat list of
 * words with their bitmap-pixel bounding boxes and the char offsets
 * each word occupies in the concatenated text. That lets the caller
 * run the existing regex / NER / forms pipeline on `text`, then map
 * each detection's `start`/`end` back to a set of bboxes via simple
 * char-range overlap.
 */

import type * as Tesseract from 'tesseract.js';
import type { BBox } from '../pii/types';

/** URL of the specific SIMD + LSTM-only tesseract core build on the
 *  jsdelivr CDN. Required when we point langPath at tessdata_best —
 *  the float-LSTM weights call DotProductSSE, which only exists in
 *  the SIMD core variants. Pinned to v7 to match the bundled
 *  tesseract.js-core dependency. */
const TESSERACT_CORE_SIMD_LSTM =
  'https://cdn.jsdelivr.net/npm/tesseract.js-core@7.0.0/tesseract-core-simd-lstm.wasm.js';

/** High-accuracy float-LSTM language data. ~22 MB (vs ~10 MB for the
 *  default integer build) and noticeably better on stylised fonts and
 *  noisy backgrounds — the kind of input we see on photos of IDs.
 *  Cached in IndexedDB after the first OCR run. */
const TESSDATA_BEST_LANG_PATH = 'https://tessdata.projectnaptha.com/4.0.0_best';

/** Probe whether the runtime can validate a minimal SIMD module. True
 *  on Chrome 91+ / Firefox 89+ / Safari 16.4+ and effectively every
 *  device shipped since 2023. Used to decide whether we can load the
 *  SIMD core + tessdata_best (high accuracy) or have to fall back to
 *  the default integer build (lower accuracy, broader compat). */
const supportsWasmSimd = (): boolean => {
  try {
    if (typeof WebAssembly === 'undefined') return false;
    const probe = new Uint8Array([
      0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1,
      8, 0, 65, 0, 253, 15, 253, 98, 11,
    ]);
    return WebAssembly.validate(probe);
  } catch {
    return false;
  }
};

/** Tesseract page-segmentation modes used by callers. Documented values
 *  from `tesseract --help-psm`; we expose the two we actually use. */
export const PSM = {
  /** PSM 3 — fully automatic page segmentation, no OSD. Tesseract's
   *  default. Best for dense documents (typed pages, scans). */
  AUTO: 3,
  /** PSM 11 — sparse text. Find as much text as possible in no
   *  particular order. Best for photos of IDs and other documents
   *  with scattered, multi-orientation text on busy backgrounds. */
  SPARSE: 11,
} as const;

export type WordBox = {
  text: string;
  bbox: BBox;
  /** Tesseract's per-word confidence in [0..1]. */
  confidence: number;
  /** Char range this word occupies in the OCR `text`. */
  charStart: number;
  charEnd: number;
};

export type OcrResult = {
  /** Concatenated word text with single-space separators. */
  text: string;
  /** Mean document confidence in [0..1]. */
  confidence: number;
  words: WordBox[];
};

export type OcrProgress = {
  /** Tesseract status string ("recognizing text", "loading language traineddata", etc.) */
  status: string;
  /** [0..1] */
  progress: number;
};

type TesseractMod = typeof Tesseract;

let modPromise: Promise<TesseractMod> | null = null;
const loadTesseract = async (): Promise<TesseractMod> => {
  modPromise ??= import('tesseract.js');
  return modPromise;
};

/** FIFO queue: each acquire() awaits the previous job's release. */
let tail: Promise<unknown> = Promise.resolve();
const acquire = async (): Promise<() => void> => {
  let release!: () => void;
  const next = new Promise<void>((resolve) => {
    release = resolve;
  });
  const prev = tail;
  tail = next;
  await prev;
  return release;
};

type RawWord = {
  text: string;
  confidence: number;
  bbox: { x0: number; y0: number; x1: number; y1: number };
};
type RawLine = { words?: RawWord[] };
type RawParagraph = { lines?: RawLine[] };
type RawBlock = { paragraphs?: RawParagraph[] };
type RawPage = {
  text?: string;
  confidence?: number;
  blocks?: RawBlock[] | null;
};

const flattenWords = (data: RawPage): WordBox[] => {
  const out: WordBox[] = [];
  let cursor = 0;
  for (const block of data.blocks ?? []) {
    for (const para of block.paragraphs ?? []) {
      for (const line of para.lines ?? []) {
        for (const w of line.words ?? []) {
          const text = w.text.trim();
          if (text.length === 0) continue;
          const start = cursor;
          const end = cursor + text.length;
          out.push({
            text,
            confidence: Math.max(0, Math.min(1, w.confidence / 100)),
            bbox: {
              x: w.bbox.x0,
              y: w.bbox.y0,
              w: w.bbox.x1 - w.bbox.x0,
              h: w.bbox.y1 - w.bbox.y0,
            },
            charStart: start,
            charEnd: end,
          });
          cursor = end + 1; // +1 accounts for the space joiner between words
        }
      }
    }
  }
  return out;
};

export type OcrSource = ImageBitmap | HTMLCanvasElement | Blob;

export type RecognizeOptions = {
  /** Tesseract page-segmentation mode. See `PSM` constants. Default
   *  `PSM.AUTO` (3) — change to `PSM.SPARSE` (11) for ID-card style
   *  inputs with scattered text on busy backgrounds. */
  pageSegMode?: number;
};

/**
 * Run OCR on an image source. Serialised via the global queue. Returns
 * an empty result on any failure rather than throwing — OCR is an
 * additive signal; we don't want a tesseract glitch to break the
 * whole dispatch.
 */
const EMPTY: OcrResult = { text: '', confidence: 0, words: [] };

export const recognize = async (
  source: OcrSource,
  onProgress?: (p: OcrProgress) => void,
  options?: RecognizeOptions,
): Promise<OcrResult> => {
  const release = await acquire();
  try {
    let mod: TesseractMod;
    try {
      mod = await loadTesseract();
    } catch (cause) {
      console.warn('[anomia] tesseract failed to load', cause);
      return EMPTY;
    }
    type WorkerLike = Awaited<ReturnType<TesseractMod['createWorker']>>;
    let worker: WorkerLike;
    try {
      // exactOptionalPropertyTypes: only set logger / paths when supplied.
      // Modern browsers (SIMD-capable wasm) get the high-accuracy
      // float LSTM build via tessdata_best + the matching SIMD core.
      // Older browsers fall back to the default integer build, which
      // works everywhere but is less accurate on stylised inputs.
      const useBest = supportsWasmSimd();
      const workerOpts: {
        logger?: (m: { status: string; progress: number }) => void;
        langPath?: string;
        corePath?: string;
      } = {};
      if (onProgress) {
        workerOpts.logger = (m) => {
          onProgress({ status: m.status, progress: m.progress });
        };
      }
      if (useBest) {
        workerOpts.langPath = TESSDATA_BEST_LANG_PATH;
        workerOpts.corePath = TESSERACT_CORE_SIMD_LSTM;
      }
      worker = await mod.createWorker('eng', 1, workerOpts);
    } catch (cause) {
      console.warn('[anomia] tesseract createWorker failed', cause);
      return EMPTY;
    }
    const psm = options?.pageSegMode;
    if (psm !== undefined) {
      try {
        await (worker.setParameters as (p: Record<string, unknown>) => Promise<unknown>)({
          tessedit_pageseg_mode: String(psm),
        });
      } catch {
        /* PSM tweak is a hint, not a blocker — fall through with default */
      }
    }
    try {
      // Tesseract's ImageLike type narrowly excludes plain ImageBitmap;
      // it accepts it at runtime so cast through unknown.
      // tesseract.js v6+ made the third arg explicit — without it some
      // builds skip the hierarchical block/paragraph/line/word tree we
      // need for per-word bboxes, and we silently get 0 words.
      const result = await (worker.recognize as (
        image: unknown,
        options?: Record<string, unknown>,
        output?: Record<string, unknown>,
      ) => Promise<{ data: unknown }>)(source, {}, { text: true, blocks: true });
      const page = result.data as RawPage;
      const words = flattenWords(page);
      const text = words.map((w) => w.text).join(' ');
      const confidence = Math.max(0, Math.min(1, (page.confidence ?? 0) / 100));
      // Diagnostic: when words.length is 0 the most likely cause is a
      // tesseract.js result shape change. Log the raw keys + sizes so
      // we can tell at a glance whether `text` came back at all and
      // whether `blocks` was populated.
      if (words.length === 0) {
        const dataObj = page as unknown as Record<string, unknown>;
        const rawText = typeof dataObj.text === 'string' ? dataObj.text : '';
        console.warn(
          `[anomia] OCR returned 0 words. data keys: [${Object.keys(dataObj).join(', ')}], ` +
            `text length: ${String(rawText.length)}, ` +
            `blocks: ${String((page.blocks ?? []).length)}, ` +
            `text sample: "${rawText.slice(0, 100).replace(/\s+/g, ' ').trim()}"`,
        );
      }
      return { text, confidence, words };
    } catch (cause) {
      console.warn('[anomia] tesseract recognise failed', cause);
      return EMPTY;
    } finally {
      try {
        await worker.terminate();
      } catch {
        /* terminate failures are non-fatal */
      }
    }
  } finally {
    release();
  }
};

/**
 * Find all word boxes that overlap a [start, end) char range. Used to
 * lift a regex/NER detection back into image space: detect on the OCR
 * text, then look up the bboxes the offsets fall within.
 */
export const wordsInRange = (
  words: readonly WordBox[],
  start: number,
  end: number,
): WordBox[] => {
  const out: WordBox[] = [];
  for (const w of words) {
    if (w.charEnd <= start) continue;
    if (w.charStart >= end) break;
    out.push(w);
  }
  return out;
};

/**
 * Stretch a canvas's luma range to maximise contrast for OCR while
 * keeping anti-aliased text strokes intact. Mutates in place.
 *
 * Pipeline: ITU-R BT.601 grayscale → linear stretch from the 5th
 * percentile of luma to the 95th. Percentile-based bounds are robust
 * to a single near-black or near-white pixel that would distort min/max.
 *
 * Earlier iterations used Otsu's method to binarise to pure black and
 * white. That helps on clean scanned documents but kills text on photos
 * with light pastel security-pattern backgrounds — the threshold lands
 * mid-luma and slices the thin parts of letters off ("Passport" reads
 * as "Passpon"). Linear stretch preserves the strokes.
 */
export const preprocessForOcr = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const w = canvas.width;
  const h = canvas.height;
  if (w === 0 || h === 0) return canvas;

  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;
  const total = w * h;

  const luma = new Uint8ClampedArray(total);
  const histogram = new Uint32Array(256);
  for (let i = 0, p = 0; p < total; i += 4, p++) {
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    const y = (0.299 * r + 0.587 * g + 0.114 * b) | 0;
    luma[p] = y;
    histogram[y] = (histogram[y] ?? 0) + 1;
  }

  // Find the 5th and 95th percentile luma values.
  const lowTarget = total * 0.05;
  const highTarget = total * 0.95;
  let cumulative = 0;
  let lo = 0;
  let hi = 255;
  let foundLo = false;
  for (let t = 0; t < 256; t++) {
    cumulative += histogram[t] ?? 0;
    if (!foundLo && cumulative >= lowTarget) {
      lo = t;
      foundLo = true;
    }
    if (cumulative >= highTarget) {
      hi = t;
      break;
    }
  }
  const range = Math.max(hi - lo, 1);

  for (let i = 0, p = 0; p < total; i += 4, p++) {
    const y = luma[p] ?? 0;
    let v = ((y - lo) * 255) / range;
    if (v < 0) v = 0;
    else if (v > 255) v = 255;
    const out = v | 0;
    data[i] = out;
    data[i + 1] = out;
    data[i + 2] = out;
    // alpha unchanged
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
};

export const _internals = { flattenWords };
